/**
 * Home-screen app update check (Sep 30 2026, Nitish; PWA refinement item 2).
 *
 * A home-screen web app (display-mode standalone, or iOS navigator.standalone) has no refresh
 * button and can keep an old build in memory for days. When it returns from the background after
 * at least MIN_HIDDEN_MS, fetch "/" uncached, read its main bundle hash, and compare it with the
 * bundle this page is running. Different = a deploy happened since this copy loaded: reload once.
 *
 * Guards:
 *   - Home-screen mode only (a browser tab has its own refresh; widen here if we ever want tabs too).
 *   - Production builds only (the dev server serves bundle.js with no hash, so nothing matches).
 *   - Shares axiosConfig's reload stamp, so this and the 426 path can never loop each other.
 *   - Any error = do nothing.
 * Drafts survive the reload: the composer writes them to localStorage on a 1s debounce, and the
 * page has been hidden for at least MIN_HIDDEN_MS before a reload can happen.
 */
import { WEB_UPDATE_RELOAD_KEY } from '../services/auth/axiosConfig';
import { getDisplayMode, trackEvent } from './analytics';

const MIN_HIDDEN_MS = 60 * 1000;
const RELOAD_COOLDOWN_MS = 60 * 1000;
const MAIN_BUNDLE_RE = /\/static\/js\/main\.([a-f0-9]+)\.js/;

let installed = false;
let hiddenAt = null;
let checking = false;

function runningBundleHash() {
  const scripts = Array.from(document.scripts || []);
  for (const s of scripts) {
    const m = MAIN_BUNDLE_RE.exec(s.src || '');
    if (m) return m[1];
  }
  return null;
}

async function checkForNewBuild() {
  if (checking) return;
  const running = runningBundleHash();
  if (!running) return; // dev build or unexpected markup
  checking = true;
  try {
    const res = await fetch('/', { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return;
    const html = await res.text();
    const m = MAIN_BUNDLE_RE.exec(html);
    if (!m || m[1] === running) return;
    const last = Number(sessionStorage.getItem(WEB_UPDATE_RELOAD_KEY) || 0);
    if (Date.now() - last <= RELOAD_COOLDOWN_MS) return;
    sessionStorage.setItem(WEB_UPDATE_RELOAD_KEY, String(Date.now()));
    trackEvent('web_update_reload', { from_build: running, to_build: m[1], trigger: 'resume' });
    window.location.reload();
  } catch (e) {
    /* offline, storage blocked, anything: stay on the current build */
  } finally {
    checking = false;
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now();
    return;
  }
  if (document.visibilityState === 'visible' && hiddenAt !== null) {
    const away = Date.now() - hiddenAt;
    hiddenAt = null;
    if (away >= MIN_HIDDEN_MS && getDisplayMode() === 'standalone') checkForNewBuild();
  }
}

/** Install once at app start. Safe to call more than once. */
export function installWebUpdateOnResume() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('visibilitychange', onVisibilityChange);
}
